import React from "react";
import { connect } from "react-redux";
import { injectIntl, useIntl } from "react-intl";
import { ColumnDefinition, RowDefinition } from "griddle-react";
import { compose } from "recompose";
import { Action } from "typescript-fsa";

import { makeDemographicsDownloadGet } from "@api/index";
import { withLocale, InjectedLocaleProps } from "@localisation/components/withLocale";
import { messages as locMessages } from "@localisation/messages";
import { LocalisationState } from "@localisation/models";
import { Collapsable } from "@ui/Table/Collapsable";
import { CellBasicNumber, CellTranslatableMap } from "@ui/Table/Cells";
import { DownloadCsvButton } from "@ui/DownloadCsvButton";
import {
    AlbumTrackVendorProps,
    makeGetRegionDataHydrated,
    getSettingFrom,
    getSettingTo,
    RegionSetPair,
} from "../../selectors";
import { messages } from "../../messages";
import { SalesState } from "../../models";

interface RegionTableOwnProps extends AlbumTrackVendorProps {
    download?: boolean;
}

interface RegionTableStateProps {
    data: RegionSetPair[];
    downloadAction: () => Action<any>;
}

type State = SalesState & LocalisationState;

type Props = RegionTableOwnProps & RegionTableStateProps & InjectedLocaleProps;

const RegionTable = ({ data, download, downloadAction, locale }: Props) => {
    const { formatMessage } = useIntl();
    if (download == null) {
        download = true;
    }
    const CellTranslatable = CellTranslatableMap(locale);

    return (
        <div className="region-table">
            <Collapsable<RegionSetPair> limit={11} data={data} layout="basic" size="small">
                <RowDefinition>
                    <ColumnDefinition
                        key="rank"
                        id="rank"
                        title="#"
                    />
                    <ColumnDefinition
                        key="name"
                        id="entity.name"
                        title={formatMessage(locMessages.regionEntitySingular)}
                    />
                    <ColumnDefinition
                        key="code"
                        id="entity.code"
                        title={formatMessage(locMessages.regionColumnCode)}
                    />
                    <ColumnDefinition
                        key="country"
                        id="entity.country.printableName"
                        title={formatMessage(locMessages.countryEntitySingular)}
                        customComponent={CellTranslatable}
                    />
                    <ColumnDefinition
                        key="total"
                        id="total"
                        title={formatMessage(messages.salesTotal)}
                        customComponent={CellBasicNumber}
                    />
                </RowDefinition>
            </Collapsable>
            {download && <DownloadCsvButton actionCreator={downloadAction} />}
        </div>
    );
};

const makeMapStateToProps = () => {
    const getRegionDataForProps = makeGetRegionDataHydrated();
    return (state: State, props: AlbumTrackVendorProps) => {
        const from = getSettingFrom(state);
        const to = getSettingTo(state);
        return {
            data: getRegionDataForProps(state, props),
            downloadAction: makeDemographicsDownloadGet(
                props.albumId,
                props.trackId,
                props.artistId,
                from,
                to,
                "region",
            ),
        } as RegionTableStateProps;
    };
};

const ConnectedRegionTable = compose<Props, RegionTableOwnProps>(
    connect(makeMapStateToProps, null),
    withLocale,
)(RegionTable);

export {
    RegionTable as RegionTablePresenter,
    ConnectedRegionTable as RegionTable,
};
